// C:\Users\aleja\Code\inventory_analytics\src\front\src\store\flux\repairableSparePartActions.js
import getApiBaseUrl from "../../utils/api";

const API_URL = getApiBaseUrl();

export const REPAIR_STATUS_LABELS = {
  BORRADOR: "Borrador",
  PENDIENTE_GESTION: "Pendiente de gestión",
  ENVIADO: "Enviado",
  PENDIENTE_PRESUPUESTO: "Pendiente de presupuesto",
  PENDIENTE_APROBACION: "Pendiente de aprobación",
  REPARACION_AUTORIZADA: "Reparación autorizada",
  EN_REPARACION: "En reparación",
  REPARADO_PENDIENTE_RECEPCION: "Reparado pendiente de recepción",
  RECIBIDO: "Recibido",
  INCIDENCIA: "Incidencia",
  CERRADO: "Cerrado",
  ANULADO: "Anulado",
};

export const REPAIR_CLOSE_RESULT_LABELS = {
  REPARADO: "Reparado",
  PRESUPUESTO_RECHAZADO: "Presupuesto rechazado",
  IRREPARABLE: "Irreparable",
  DEVUELTO_SIN_REPARAR: "Devuelto sin reparar",
  SUSTITUIDO_POR_COMPRA_NUEVA: "Sustituido por compra nueva",
  OTRO: "Otro",
};

export const REPAIR_DOCUMENT_TYPE_LABELS = {
  PRESUPUESTO: "Presupuesto",
  ALBARAN_SALIDA: "Albarán de salida",
  DIAGNOSTICO: "Diagnóstico",
  INFORME_REPARACION: "Informe de reparación",
  ALBARAN_RECEPCION: "Albarán de recepción",
  FACTURA: "Factura",
  FOTOGRAFIA: "Fotografía",
  OTRO: "Otro",
};

export const REPAIR_COMMENT_CATEGORY_LABELS = {
  TECNICO: "Técnico",
  PROVEEDOR: "Proveedor",
  ADMINISTRATIVO: "Administrativo",
  SEGUIMIENTO: "Seguimiento",
  RECEPCION: "Recepción",
  OTRO: "Otro",
};

const appendParams = (url, params = {}) => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item !== null && item !== undefined && item !== "") {
          searchParams.append(key, String(item));
        }
      });
      return;
    }

    if (value !== null && value !== undefined && value !== "") {
      searchParams.set(key, String(value));
    }
  });

  const queryString = searchParams.toString();
  return `${url}${queryString ? `?${queryString}` : ""}`;
};

const normalizeError = (payload, fallbackMessage) => {
  const error = payload?.error;

  if (typeof error === "string" && error.trim()) {
    return {
      message: error,
      code: null,
      details: null,
    };
  }

  if (error && typeof error === "object") {
    return {
      message:
        error.message ||
        error.detail ||
        error.code ||
        fallbackMessage,
      code: error.code || null,
      details: error.details || null,
    };
  }

  if (typeof payload?.msg === "string" && payload.msg.trim()) {
    return {
      message: payload.msg,
      code: null,
      details: null,
    };
  }

  return {
    message: fallbackMessage,
    code: null,
    details: null,
  };
};

const getFilenameFromDisposition = (response, fallbackFilename) => {
  const disposition = response.headers.get("content-disposition") || "";
  const utfMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i);

  if (utfMatch?.[1]) {
    try {
      return decodeURIComponent(utfMatch[1].replace(/["']/g, ""));
    } catch {
      return utfMatch[1].replace(/["']/g, "");
    }
  }

  const standardMatch = disposition.match(/filename="?([^";]+)"?/i);
  return standardMatch?.[1] || fallbackFilename;
};

const repairableSparePartActions = ({ getStore, getActions }) => {
  const requestJson = async ({
    path,
    method = "GET",
    params = {},
    body,
    formData,
    signal,
    fallbackMessage = "No se pudo completar la operación",
    successMessage,
    acceptLegacyData = false,
  }) => {
    try {
      const token = getStore().token;
      const headers = {
        Authorization: `Bearer ${token}`,
      };

      if (!formData && body !== undefined) {
        headers["Content-Type"] = "application/json";
      }

      const response = await fetch(
        appendParams(`${API_URL}${path}`, params),
        {
          method,
          cache: "no-store",
          headers,
          body: formData || (body !== undefined ? JSON.stringify(body) : undefined),
          signal,
        },
      );

      if (response.status === 401) {
        await getActions().handleApiError(response);
        return {
          success: false,
          data: null,
          error: "La sesión ha caducado",
          code: null,
          details: null,
          status: 401,
        };
      }

      const payload = await response.json().catch(() => null);
      const hasStandardSuccess = payload?.success === true;

      if (!response.ok || (!hasStandardSuccess && !acceptLegacyData)) {
        const normalized = normalizeError(payload, fallbackMessage);

        if (response.status === 403) {
          getActions().showToast(
            "No tienes permisos para realizar esta acción",
            "error",
          );
        }

        return {
          success: false,
          data: payload?.data ?? null,
          error: normalized.message,
          code: normalized.code,
          details: normalized.details,
          status: response.status,
        };
      }

      if (successMessage) {
        getActions().showToast(successMessage, "success");
      }

      return {
        success: true,
        data: hasStandardSuccess ? payload.data ?? null : payload,
        error: null,
        code: null,
        details: null,
        status: response.status,
      };
    } catch (error) {
      if (error.name === "AbortError") {
        return {
          success: false,
          data: null,
          error: "Request aborted",
          aborted: true,
          status: 0,
        };
      }

      console.error("Repairable spare-part API error:", error);
      return {
        success: false,
        data: null,
        error: fallbackMessage,
        code: null,
        details: null,
        status: 0,
      };
    }
  };

  const requestBlob = async ({
    path,
    params = {},
    signal,
    fallbackFilename,
    fallbackMessage,
  }) => {
    try {
      const token = getStore().token;
      const response = await fetch(
        appendParams(`${API_URL}${path}`, params),
        {
          method: "GET",
          cache: "no-store",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          signal,
        },
      );

      if (response.status === 401) {
        await getActions().handleApiError(response);
        return {
          success: false,
          data: null,
          error: "La sesión ha caducado",
          status: 401,
        };
      }

      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        const normalized = normalizeError(payload, fallbackMessage);
        return {
          success: false,
          data: null,
          error: normalized.message,
          code: normalized.code,
          status: response.status,
        };
      }

      const blob = await response.blob();
      return {
        success: true,
        data: {
          blob,
          objectUrl: window.URL.createObjectURL(blob),
          filename: getFilenameFromDisposition(response, fallbackFilename),
          contentType: response.headers.get("content-type") || blob.type || "",
        },
        error: null,
        status: response.status,
      };
    } catch (error) {
      if (error.name === "AbortError") {
        return {
          success: false,
          data: null,
          error: "Request aborted",
          aborted: true,
          status: 0,
        };
      }

      console.error("Repairable spare-part document error:", error);
      return {
        success: false,
        data: null,
        error: fallbackMessage,
        status: 0,
      };
    }
  };

  return {
    getRepairableSparePartOptions: (options = {}) =>
      requestJson({
        path: "/api/repairable-spare-parts/options",
        signal: options.signal,
        fallbackMessage: "No se pudieron cargar las opciones del módulo",
      }),

    listRepairableSpareParts: (params = {}, options = {}) =>
      requestJson({
        path: "/api/repairable-spare-parts",
        params,
        signal: options.signal,
        fallbackMessage: "No se pudo cargar el listado de reparaciones",
      }),

    listDeletedRepairableSpareParts: (params = {}, options = {}) =>
      requestJson({
        path: "/api/repairable-spare-parts/deleted",
        params,
        signal: options.signal,
        fallbackMessage: "No se pudo cargar el historial de eliminados",
      }),

    deleteRepairableSparePart: (caseId, payload) =>
      requestJson({
        path: `/api/repairable-spare-parts/${caseId}`,
        method: "DELETE",
        body: payload,
        fallbackMessage: "No se pudo eliminar el expediente",
        successMessage: "Expediente eliminado correctamente",
      }),

    createRepairableSparePart: (payload) =>
      requestJson({
        path: "/api/repairable-spare-parts",
        method: "POST",
        body: payload,
        fallbackMessage: "No se pudo crear el expediente",
        successMessage: "Expediente creado correctamente",
      }),

    getRepairableSparePart: (caseId, options = {}) =>
      requestJson({
        path: `/api/repairable-spare-parts/${caseId}`,
        signal: options.signal,
        fallbackMessage: "No se pudo cargar el expediente",
      }),

    updateRepairableSparePart: (caseId, payload) =>
      requestJson({
        path: `/api/repairable-spare-parts/${caseId}`,
        method: "PATCH",
        body: payload,
        fallbackMessage: "No se pudo actualizar el expediente",
        successMessage: "Expediente actualizado correctamente",
      }),

    getRepairableSparePartEconomics: (caseId, options = {}) =>
      requestJson({
        path: `/api/repairable-spare-parts/${caseId}/economics`,
        signal: options.signal,
        fallbackMessage: "No se pudieron cargar los datos económicos",
      }),

    transitionRepairableSparePart: (caseId, payload) =>
      requestJson({
        path: `/api/repairable-spare-parts/${caseId}/transition`,
        method: "POST",
        body: payload,
        fallbackMessage: "No se pudo cambiar el estado",
        successMessage: "Estado actualizado correctamente",
      }),

    updateRepairableSparePartResponsible: (caseId, payload) =>
      requestJson({
        path: `/api/repairable-spare-parts/${caseId}/responsible`,
        method: "PATCH",
        body: payload,
        fallbackMessage: "No se pudo cambiar el responsable",
        successMessage: "Responsable actualizado correctamente",
      }),

    createRepairableSparePartCorrection: (caseId, payload) =>
      requestJson({
        path: `/api/repairable-spare-parts/${caseId}/corrections`,
        method: "POST",
        body: payload,
        fallbackMessage: "No se pudo registrar la corrección",
        successMessage: "Corrección registrada correctamente",
      }),

    getRepairableSparePartHistory: (caseId, options = {}) =>
      requestJson({
        path: `/api/repairable-spare-parts/${caseId}/history`,
        signal: options.signal,
        fallbackMessage: "No se pudo cargar el historial",
      }),

    getRepairableSparePartCorrections: (caseId, options = {}) =>
      requestJson({
        path: `/api/repairable-spare-parts/${caseId}/corrections`,
        signal: options.signal,
        fallbackMessage: "No se pudieron cargar las correcciones",
      }),

    getRepairableSparePartComments: (caseId, options = {}) =>
      requestJson({
        path: `/api/repairable-spare-parts/${caseId}/comments`,
        signal: options.signal,
        fallbackMessage: "No se pudieron cargar los comentarios",
      }),

    createRepairableSparePartComment: (caseId, payload) =>
      requestJson({
        path: `/api/repairable-spare-parts/${caseId}/comments`,
        method: "POST",
        body: payload,
        fallbackMessage: "No se pudo añadir el comentario",
        successMessage: "Comentario añadido correctamente",
      }),

    getRepairableSparePartDocuments: (caseId, params = {}, options = {}) =>
      requestJson({
        path: `/api/repairable-spare-parts/${caseId}/documents`,
        params,
        signal: options.signal,
        fallbackMessage: "No se pudieron cargar los documentos",
      }),

    uploadRepairableSparePartDocument: (caseId, formData) =>
      requestJson({
        path: `/api/repairable-spare-parts/${caseId}/documents`,
        method: "POST",
        formData,
        fallbackMessage: "No se pudo subir el documento",
        successMessage: "Documento subido correctamente",
      }),

    previewRepairableSparePartDocument: (caseId, documentId, options = {}) =>
      requestBlob({
        path: `/api/repairable-spare-parts/${caseId}/documents/${documentId}/preview`,
        signal: options.signal,
        fallbackFilename: "vista-previa",
        fallbackMessage: "No se pudo cargar la vista previa",
      }),

    downloadRepairableSparePartDocument: async (
      caseId,
      documentId,
      fallbackFilename = "documento",
    ) => {
      const result = await requestBlob({
        path: `/api/repairable-spare-parts/${caseId}/documents/${documentId}/download`,
        fallbackFilename,
        fallbackMessage: "No se pudo descargar el documento",
      });

      if (!result.success) {
        return result;
      }

      const anchor = document.createElement("a");
      anchor.href = result.data.objectUrl;
      anchor.download = result.data.filename || fallbackFilename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => {
        window.URL.revokeObjectURL(result.data.objectUrl);
      }, 0);

      return {
        ...result,
        data: {
          filename: result.data.filename,
          contentType: result.data.contentType,
        },
      };
    },

    deleteRepairableSparePartDocument: (caseId, documentId, reason) =>
      requestJson({
        path: `/api/repairable-spare-parts/${caseId}/documents/${documentId}`,
        method: "DELETE",
        body: { reason },
        fallbackMessage: "No se pudo eliminar el documento",
        successMessage: "Documento eliminado lógicamente",
      }),

    searchRepairableOrmaMaterials: (query, options = {}) =>
      requestJson({
        path: "/api/catalog/orma-catalog/search",
        params: {
          q: query,
          page: 1,
          per_page: 20,
          sort_by: "material_id",
          sort_order: "asc",
        },
        signal: options.signal,
        fallbackMessage: "No se pudo buscar en el catálogo ORMA",
      }),

    getRepairableCurrentUser: (options = {}) =>
      requestJson({
        path: "/api/me",
        signal: options.signal,
        fallbackMessage: "No se pudo cargar el usuario actual",
        acceptLegacyData: true,
      }),

    getRepairableResponsibleUsers: (options = {}) =>
      requestJson({
        path: "/api/users",
        signal: options.signal,
        fallbackMessage: "No se pudieron cargar los usuarios responsables",
        acceptLegacyData: true,
      }),
  };
};

export default repairableSparePartActions;
